<template>
    <div>
        <v-data-table
            :headers="headers"
            :items="reports"
            :sort-by="['academic_period', 'generated_at']"
            :sort-desc="[true, true]"
            multi-sort
            class="table"
            :class="{'table-disabled': flags.loading_reports}"
            hide-default-footer
            :loading="flags.loading_reports"
            :loading-text="$t('reports.reports_list.loading')"
        >
            <template #no-data>
                <span>{{ $t('reports.reports_list.no_records_found') }}</span>
            </template>

            <template #item.created_at="{ item }">
                {{ getReportCreationDate(item.created_at) }}
            </template>

            <template #item.status="{ item }">
                <v-chip
                    :color="getReportStatusBadgeColor(item.status)"
                    text-color="white"
                    class="px-0"
                    label
                    small
                >
                    <span class="text-center status-chip">{{ getReportStatusTranslation(item.status) }}</span>
                </v-chip>
            </template>

            <template #item.actions="{ item }">
                <div class='d-flex justify-end'>
                    <v-btn
                        v-if="checkPermission('REPORTS_VIEW')"
                        class='btn-icon--primary btn-icon'
                        @click='showReportView(item.id)'
                    ><v-icon>mdi-pencil</v-icon></v-btn>

                    <v-btn
                        v-if="checkPermission('REPORTS_DELETE')"
                        :disabled="!canBeDeleted(item)"
                        class='btn-icon--delete btn-icon ml-3'
                        @click='showDeleteConfirmationDialogFor(item)'
                    ><v-icon>mdi-delete</v-icon></v-btn>
                </div>
            </template>

        </v-data-table>

        <delete-report-dialog
            :showDialog="flags.deleteReportDialog"
            :report="reportToDelete"
            v-on:cancelReportDeletion="hideReportDeletionDialog"
            v-on:confirmReportDeletion="removeReport"
        />

        <div class="text-center pt-2">
            <v-pagination
                v-model="pagination.page"
                class="pagination"
                :length="pagination.pageCount"
                :total-visible="pagination.totalVisible"
                :disabled="flags.loading_reports"
                @input="onPaginationChange"
            ></v-pagination>
        </div>
    </div>
</template>

<script>
import DeleteReportDialog from "./DeleteReportDialog";
import {EventBus} from "@/event-bus";
import {mapActions} from 'pinia';
import {ReportsStore} from "@/Store/Reports/ReportsStore";
import toastMixin from "@/Shared/toastMixin";
import {i18n} from "@/i18n";
import _ from "lodash";
import {mapState} from "pinia/dist/pinia";
import {PermissionsStore} from "@/Store/PermissionsStore";

export default {
    name: "ReportsTable",

    components: {DeleteReportDialog},

    props: {
        type: {
            type: Object,
            required: true,
        },
    },

    mixins: [toastMixin],

    data () {
        return {
            filters: {
                report_type: this.type.id,
                academic_periods: [],
                statuses: [],
            },

            flags: {
                loading_reports: false,
                deleteReportDialog: false,
            },

            headers: [
                { text: i18n.t('reports.reports_list.headers.title'), align: 'start', sortable: true, value: 'title' },
                { text: i18n.t('reports.reports_list.headers.academic_period'), sortable: false, value: 'academic_period.name' },
                { text: i18n.t('reports.reports_list.headers.generated_at'), value: 'created_at' },
                { text: i18n.t('reports.reports_list.headers.generated_by'), sortable: false, value: 'user.username' },
                { text: i18n.t('reports.reports_list.headers.status'), sortable: false, value: 'status' },
                { align: 'center', sortable: false, value:'actions'}
            ],

            reports: [],

            pagination: {
                page: 1,
                itemsPerPage: 6,
                pageCount: null,
                itemsLength: null,
                totalVisible: 7,
            },

            reportToDelete: {},
        }
    },

    methods: {

        /**
         * Set filters for searching reports
         * based on criteria
         *
         * @param filters
         */
        applyFilters(filters) {
            this.filters = Object.assign(this.filters, filters);

            this.pagination.page = 1;

            this.fetchReports();
        },

        /**
         * Gets reports on pagination change
         */
        onPaginationChange() {
            this.fetchReports();
        },

        /**
         * Search reports based on filters
         */
        async fetchReports() {
            try {
                this.flags.loading_reports = true;

                let response = await this.searchReports({
                    filters: _.cloneDeep(this.filters),
                    pagination:  _.cloneDeep(this.pagination)
                });

                this.reports = _.get(response, 'data._embedded.reports');

                this.pagination.page = _.get(response, 'data.pagination.current_page');
                this.pagination.itemsLength = _.get(response, 'data.pagination.total');
                this.pagination.pageCount = _.get(response, 'data.pagination.total_pages');

            } catch (ex) {

                this.toast().error(
                    this.$t('reports.reports_list.error_search_title'),
                    this.$t('reports.reports_list.error_search_message'),
                );

            } finally {

                this.flags.loading_reports = false;
            }
        },

        /**
         *
         * @param status
         * @returns {*}
         */
        getReportStatusBadgeColor(status) {
            let colorsByStatus = {
                IN_PROCESS: 'primary',
                GENERATED: 'success',
                IN_REVIEW: 'warning',
                DEPRECATED: 'secondary',
                FAILED: 'error',
                CLOSED: 'blue-grey',
            }

            return colorsByStatus[status];
        },

        /**
         *
         * @param status
         * @returns {*}
         */
        getReportStatusTranslation(status) {
            return i18n.t(`reports.statuses.${status}`);
        },

        /**
         *
         * @param creationDatetime
         * @returns {*}
         */
        getReportCreationDate(creationDatetime) {
            return creationDatetime.slice(0, 10);
        },

        /**
         *
         * @param reportId
         */
        showReportView(reportId) {
            this.$inertia.get(
                route('reports.show', {'report': reportId, type: this.reportTypeRouteParameter}),
            );
        },

        /**
         * Shows dialog to delete given report
         */
        showDeleteConfirmationDialogFor(report) {
            this.reportToDelete = _.cloneDeep(report);

            this.flags.deleteReportDialog = true;
        },

        /**
         *
         */
        hideReportDeletionDialog() {
            this.reportToDelete = {};

            this.flags.deleteReportDialog = false;
        },

        /**
         * Deletes selected report
         *
         */
        async removeReport() {
            try {

                await this.deleteReport(_.cloneDeep(this.reportToDelete));

                this.toast().success(
                    this.$t('reports.reports_list.success_delete_title', {reportTitle: this.reportToDeleteTitle}),
                );

            } catch (ex) {
                console.log(ex);

                this.toast().error(
                    this.$t('reports.reports_list.error_delete_title', {reportTitle: this.reportToDeleteTitle}),
                    this.$t('reports.reports_list.error_delete_message'),
                );

            } finally {

                EventBus.$emit('reports.management.delete-report');

                this.hideReportDeletionDialog();

                await this.fetchReports();
            }
        },

        /**
         *  Add headers items to table depending on report type
         */
        addCustomHeaderItems(){
            if(this.type.name === 'ANNUAL_DEGREE_IMPROVEMENT')
                this.headers.splice(2,0,
                    {text: i18n.t('reports.reports_list.headers.program'), sortable: false, value: 'program.abbreviation'},
                );
        },

        /**
         *
         * @param Report
         * @returns {boolean}
         */
        canBeDeleted(Report) {
            return !['IN_PROCESS', 'FAILED'].includes(Report.status);
        },

        /**
         *
         */
        ...mapActions(ReportsStore, [
            'searchReports',
            'deleteReport'
        ]),

        ...mapActions(PermissionsStore, ['checkPermission']),
    },

    created() {
        EventBus.$on('reports.management.filter-reports', this.applyFilters);

        this.fetchReports();

        this.addCustomHeaderItems();
    },

    mounted() {

    },

    computed: {
        /**
         *
         * @returns {*}
         */
        reportTypeRouteParameter() {
            return _.get(this.type, 'route_type_parameter');
        },

        /**
         *
         * @returns {*}
         */
        reportToDeleteTitle() {
            return _.get(this.reportToDelete, 'title');
        }
    }
}
</script>

<style scoped>

    .table-disabled {
        opacity: 0.5;
    }

    .status-chip {
        min-width: 90px;
    }
</style>
